import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import AOS from "aos";
import MainLayout from "./components/layout/MainLayout";
import ScrollToTop from "./ScrollToTop";

// Home is always needed — keep static
import Home from "./features/home/Home";

// All other routes are lazy-loaded so they don't block the home page
const About                 = lazy(() => import("./features/aboutus/AboutUs"));
const AboutAdministrator    = lazy(() => import("./features/aboutus/aboutAdministrator/AboutAdministrator"));
const AmanahInSotor         = lazy(() => import("./features/aboutus/amanahInSotor/AmanahInSotor"));
const VisualIdentity        = lazy(() => import("./features/aboutus/VisualIdentity"));
const AmanahAgents          = lazy(() => import("./features/aboutus/agents/AmanahAgents"));
const Structure             = lazy(() => import("./features/aboutus/structure").then(m => ({ default: m.Structure })));
const AboutAmnaVision       = lazy(() => import("./features/aboutus/aboutAmnaVision/AboutAmnaVision"));
const Rules                 = lazy(() => import("./features/rules/Rules"));
const ContactUs             = lazy(() => import("./features/contact/ContactUs"));
const AmanahProject         = lazy(() => import("./features/amanahProject/AmanahProject"));
const ProjectDetails        = lazy(() => import("./features/amanahProject/ProjectDetails").then(m => ({ default: m.ProjectDetails })));
const ElectronicServices    = lazy(() => import("./features/electronicServices/ElectronicServices"));
const ServiceDetails        = lazy(() => import("./features/serviceDetails/ServiceDetails"));
const AmanahEvent           = lazy(() => import("./features/amanahEvent/AmanahEvent"));
const AmanahEventArc        = lazy(() => import("./features/amanahEvent/AmanahEventArc"));
const EventDetails          = lazy(() => import("./features/amanahEvent/EventDetails").then(m => ({ default: m.EventDetails })));
const AdDetails             = lazy(() => import("./features/amanahAdvertisement/AdDetails").then(m => ({ default: m.AdDetails })));
const Investment            = lazy(() => import("./features/amanahAdvertisement/Investment").then(m => ({ default: m.Investment })));
const Manqolat              = lazy(() => import("./features/amanahAdvertisement/Manqolat").then(m => ({ default: m.Manqolat })));
const Poll                  = lazy(() => import("./features/amanahAdvertisement/Poll").then(m => ({ default: m.Poll })));
const Qualification         = lazy(() => import("./features/amanahAdvertisement/Qualification").then(m => ({ default: m.Qualification })));
const Surveys               = lazy(() => import("./features/amanahAdvertisement/Surveys").then(m => ({ default: m.Surveys })));
const EngOffices            = lazy(() => import("./features/amanahAdvertisement/EngOffices").then(m => ({ default: m.EngOffices })));
const Locations             = lazy(() => import("./features/amanahAdvertisement/Locations").then(m => ({ default: m.Locations })));
const News                  = lazy(() => import("./features/news/News"));
const NewsDetails           = lazy(() => import("./features/news/NewsDetails").then(m => ({ default: m.NewsDetails })));
const SocialMedia           = lazy(() => import("./features/socialMedia/SocialMedia"));
const EParticipation        = lazy(() => import("./features/EParticipation/EParticipation"));
const EParticipationPolicy  = lazy(() => import("./features/EParticipation/EParticipationPolicy"));
const Consultations         = lazy(() => import("./features/EParticipation/Consultations"));
const COCreation            = lazy(() => import("./features/EParticipation/COCreation"));
const OpenData              = lazy(() => import("./features/EParticipation/OpenData"));
const VoiceReports          = lazy(() => import("./features/EParticipation/VoiceReports"));
const ServicesStatistics    = lazy(() => import("./features/EParticipation/ServicesStatistics"));
const PrivacyPolicy         = lazy(() => import("./features/privacyPolicy/PrivacyPolicy"));
const ServiceLevelAgreement = lazy(() => import("./features/serviceLevelAgreement/ServiceLevelAgreement"));
const TermsOfUse            = lazy(() => import("./features/termsOfUse/TermsOfUse"));
const SearchPage            = lazy(() => import("./features/searchPage/searchPage").then(m => ({ default: m.SearchPage })));
const SiteMap               = lazy(() => import("./features/siteMap/SiteMap"));
const FAQ                   = lazy(() => import("./features/faq/FAQ"));
const KnowledgeCenter       = lazy(() => import("./features/knowledgeCenter/KnowledgeCenter"));
const NotFoun404            = lazy(() => import("./features/notFoun404/NotFoun404"));

function App() {
  useEffect(() => {
    if (document.fonts) {
      document.fonts.ready.then(() => document.body.classList.add("fonts-loaded"));
    } else {
      setTimeout(() => document.body.classList.add("fonts-loaded"), 500);
    }
    if (!window.__aosInitialized) {
      AOS.init({ once: true, duration: 800, offset: 200, easing: "ease-in-out" });
      window.__aosInitialized = true;
    }
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<MainLayout />}>
            <Route index                            element={<Home />} />
            <Route path="about"                     element={<About />} />
            <Route path="about-administrator"       element={<AboutAdministrator />} />
            <Route path="amanah-in-sotor"           element={<AmanahInSotor />} />
            <Route path="about/visual-identity"     element={<VisualIdentity />} />
            <Route path="amanah-agents"             element={<AmanahAgents />} />
            <Route path="about/structure"           element={<Structure />} />
            <Route path="rules"                     element={<Rules />} />
            <Route path="contact"                   element={<ContactUs />} />
            <Route path="projects"                  element={<AmanahProject />} />
            <Route path="project-details"           element={<ProjectDetails />} />
            <Route path="electronic-services"       element={<ElectronicServices />} />
            <Route path="service-details/:id"       element={<ServiceDetails />} />
            <Route path="events"                    element={<AmanahEvent />} />
            <Route path="events_arc"                element={<AmanahEventArc />} />
            <Route path="event-details/:id"         element={<EventDetails />} />
            <Route path="event-details"             element={<EventDetails />} />
            <Route path="ad-details/:id"            element={<AdDetails />} />
            <Route path="ad-investment"             element={<Investment />} />
            <Route path="manqolat"                  element={<Manqolat />} />
            <Route path="poll"                      element={<Poll />} />
            <Route path="qualification"             element={<Qualification />} />
            <Route path="surveys"                   element={<Surveys />} />
            <Route path="eng-offices"               element={<EngOffices />} />
            <Route path="locations"                 element={<Locations />} />
            <Route path="news"                      element={<News />} />
            <Route path="news-details"              element={<NewsDetails />} />
            <Route path="/social-media"             element={<SocialMedia />} />
            <Route path="e-participation"           element={<EParticipation />} />
            <Route path="e-participation-policy"    element={<EParticipationPolicy />} />
            <Route path="consultations"             element={<Consultations />} />
            <Route path="co-creation"               element={<COCreation />} />
            <Route path="open-data"                 element={<OpenData />} />
            <Route path="voice-reports"             element={<VoiceReports />} />
            <Route path="services-statistics"       element={<ServicesStatistics />} />
            <Route path="vision"                    element={<AboutAmnaVision />} />
            <Route path="privacy-policy"            element={<PrivacyPolicy />} />
            <Route path="search-page"               element={<SearchPage />} />
            <Route path="service-level-agreement"   element={<ServiceLevelAgreement />} />
            <Route path="terms-of-use"              element={<TermsOfUse />} />
            <Route path="site-map"                  element={<SiteMap />} />
            <Route path="faq"                       element={<FAQ />} />
            <Route path="knowledge-center"          element={<KnowledgeCenter />} />
            <Route path="*"                         element={<NotFoun404 />} />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
